import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  EdiTemplateCandidate,
  getEdiTemplateCandidates,
  applyEdiTemplate,
} from '../services/pendingEdiOrders.service';

// =============================================================================
// EdiTemplatePickerModal
// =============================================================================
// The Get Template drop-down (V1 edi_gettemplate lookup table). Shows the approved
// templates matching a pending EDI order's partner rules; picking one stamps it onto the
// target recnos (applyEdiTemplate). Used by BOTH single Get Template (one row + [recno])
// and Get Template For All (the bucket's shared params + all eligible recnos) — same
// component, same candidate call, same apply call. Portaled at z-[130] so it sits above the
// drill drawer (a noBackdrop z-[100] portal on the LC Dashboard / board drawer view).

export interface EdiTemplatePickerParams {
  edihandle: string;
  billto: number;
  movetype: string;
  deliveryrecno: number;
}

interface Props {
  /** Bucket params the candidate list is matched on (shared across the target rows). */
  params: EdiTemplatePickerParams;
  /** Rows to stamp — single = [recno]; "…For All" = the eligible set. */
  recnos: number[];
  /** Header context (e.g. "WALMART — walmartedi · 5 orders"). */
  title: string;
  /** The row's current template when editing a single set order — enables "Clear Template". */
  currentTemplate?: string;
  /** true (Get Template For All) stamps only blank rows; false (single edit) may change/clear. */
  onlyBlank?: boolean;
  onClose: () => void;
  /** Fired after a template is applied so the host can refresh. */
  onApplied: () => void;
}

const fmtMoney = (v: unknown): string => {
  if (v === '' || v == null) return '';
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isNaN(n) ? '' : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const destBlock = (r: EdiTemplateCandidate): string =>
  [r.destname, r.destadd, r.destcitystatezip].map((x) => (x == null ? '' : String(x).trim())).filter(Boolean).join(' · ');

const EdiTemplatePickerModal: React.FC<Props> = ({ params, recnos, title, currentTemplate, onlyBlank = true, onClose, onApplied }) => {
  const [rows, setRows] = useState<EdiTemplateCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applyingPro, setApplyingPro] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);
  const [filter, setFilter] = useState('');
  const busy = applyingPro != null || clearing;
  const hasCurrent = !!(currentTemplate && currentTemplate.trim());

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    getEdiTemplateCandidates(params.edihandle, params.billto, params.movetype, params.deliveryrecno)
      .then((resp) => { if (alive) setRows(resp.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || e?.message || 'Failed to load templates.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [params.edihandle, params.billto, params.movetype, params.deliveryrecno]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.pronumber, r.cname, r.csr, r.templatetype, r.comments, destBlock(r)]
        .some((v) => String(v ?? '').toLowerCase().includes(q)));
  }, [rows, filter]);

  const apply = (pro: string, isClear = false) => {
    if (busy) return;
    if (isClear) setClearing(true); else setApplyingPro(pro);
    applyEdiTemplate(recnos, pro, onlyBlank)
      .then(() => { onApplied(); onClose(); })
      .catch((e) => {
        setError(e?.response?.data?.detail || e?.message || `Failed to ${isClear ? 'clear' : 'apply'} template.`);
        setApplyingPro(null); setClearing(false);
      });
  };
  const pick = (pro: string) => apply(pro, false);

  const TH = 'px-2 py-1.5 text-left text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 whitespace-nowrap';
  const TD = 'px-2 py-1.5 align-top text-slate-700 dark:text-gray-200';

  return createPortal(
    <div
      className="fixed inset-0 z-[130] bg-slate-900/40 flex items-center justify-center p-6"
      role="dialog"
      aria-label="Get Template"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-slate-200 dark:border-gray-700 w-full max-w-6xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-heading">Get Template</h2>
            <p className="text-xs text-slate-500 dark:text-gray-400 truncate">{title}</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter templates…"
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
            />
            <button type="button" onClick={() => { if (!busy) onClose(); }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" aria-label="Close">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-auto">
          {error && (
            <div className="m-3 rounded-md border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
              {error}
            </div>
          )}
          {loading ? (
            <div className="p-8 text-center text-sm text-muted">Loading templates…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted">
              {rows.length === 0 ? 'No matching approved templates found for this partner.' : 'No templates match the filter.'}
            </div>
          ) : (
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 bg-slate-100 dark:bg-slate-800 z-10">
                <tr>
                  <th className={TH}>Template</th>
                  <th className={TH}>Move Type</th>
                  <th className={TH}>Rate Zone</th>
                  <th className={TH}>CSR</th>
                  <th className={TH}>Customer</th>
                  <th className={`${TH} text-right`}>Container Dray</th>
                  <th className={`${TH} text-right`}>Invoice Total</th>
                  <th className={TH}>Pickup</th>
                  <th className={TH}>Destination</th>
                  <th className={TH}>Comments</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {filtered.map((r) => (
                  <tr key={r.pronumber} className="hover:bg-blue-50 dark:hover:bg-slate-800/60">
                    <td className={TD}>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => pick(r.pronumber)}
                        className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 hover:bg-blue-700 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
                        title={`Apply template ${r.pronumber}`}
                      >
                        {applyingPro === r.pronumber ? (
                          <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> …</>
                        ) : r.pronumber}
                      </button>
                    </td>
                    <td className={`${TD} whitespace-nowrap`}>{r.movetype}</td>
                    <td className={`${TD} whitespace-nowrap`}>{r.templatetype}</td>
                    <td className={`${TD} whitespace-nowrap`}>{r.csr}</td>
                    <td className={TD}>{r.cname}</td>
                    <td className={`${TD} text-right whitespace-nowrap tabular-nums`}>{fmtMoney(r.drayrate)}</td>
                    <td className={`${TD} text-right whitespace-nowrap tabular-nums`}>{fmtMoney(r.invoicetotal)}</td>
                    <td className={TD}>{r.origname}</td>
                    <td className={TD}>{destBlock(r)}</td>
                    <td className={`${TD} max-w-[16rem]`}><span className="line-clamp-2">{r.comments}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-slate-200 dark:border-gray-700">
          <span className="text-xs text-slate-500 dark:text-gray-400">
            {hasCurrent && <>Current: <span className="font-medium text-slate-700 dark:text-gray-200">{currentTemplate}</span> · </>}
            {recnos.length === 1 ? 'Applies to 1 order' : `Applies to ${recnos.length} orders${onlyBlank ? ' (blank-template only)' : ''}`}
            {!loading && ` · ${filtered.length} template${filtered.length === 1 ? '' : 's'}`}
          </span>
          <div className="flex items-center gap-2">
            {hasCurrent && (
              <button type="button" onClick={() => apply('', true)} disabled={busy}
                className="px-4 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-panel border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50 inline-flex items-center gap-2">
                {clearing ? (<><div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" /> Clearing…</>) : 'Clear Template'}
              </button>
            )}
            <button type="button" onClick={() => { if (!busy) onClose(); }} disabled={busy}
              className="px-4 py-1.5 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EdiTemplatePickerModal;
